/**
 * Report an API read-boundary schema mismatch to Sentry (AMP-593).
 *
 * This is the instrument the report-only boundary exists to feed: production is
 * the only place that enumerates the data states our static audits keep
 * missing, so every mismatch we absorb still has to be visible and actionable.
 *
 * Two properties keep the signal usable:
 *
 * - **Fingerprint by (kind, field path, error code), never by message.** Zod
 *   messages embed received values, so message-grouping would shatter one real
 *   gap into thousands of issues. Grouping by the triple means "ticket.property
 *   is null" is exactly one Sentry issue no matter how many rows hit it.
 * - **Array indexes are normalised out of the fingerprint.** A drifted list
 *   yields `results.0.property`, `results.1.property`, … — one real gap, one
 *   issue per row, which would defeat the grouping above and exhaust the session
 *   dedupe set on a single response. The exact index still ships in `extra`.
 * - **Record (map) keys are collapsed everywhere — they are PII (AMP-638).** A
 *   `z.record`'s keys are arbitrary data, not schema structure, and at least one
 *   response is a dict keyed by CUSTOMER EMAIL (`notesByUser`). A key is not
 *   numeric, so the index rule above misses it; left alone it would ship the
 *   email into `extra.path`, the `api.path` tag AND the durable fingerprint.
 *   Record-key segments collapse to `*` in ALL three sinks, detected by walking
 *   the schema alongside the path (the key text cannot flag itself).
 * - **First occurrence always reported, repeats sampled.** Coverage is never
 *   lost; volume is. See `schemaMismatchSampleRates`.
 *
 * Values are NOT forwarded, to Sentry OR to the console. Zod's free-text
 * `message` embeds received values (`Invalid enum value … received 'X'`), and so
 * does `received` on every issue code except `invalid_type`, where it is a type
 * name rather than data. Only the structured, schema-side fields — path, code,
 * and `expected`/`received` on `invalid_type` alone — are ever printed or sent.
 *
 * The console used to print the free-text `message`, on the reasoning that local
 * dev is not Sentry. It no longer does: a dev console is screen-shared, pasted
 * into tickets and Slack, and captured by session replay, so the same rule holds
 * on both paths. Nothing is lost — the structured fields carry the type mismatch
 * that `message` was being read for.
 */
import { captureMessage } from '@sentry/react';
import { z } from 'zod';

import { schemaMismatchSampleRates } from './apiBoundaryConfig';

/**
 * Which pass produced the mismatch. `relaxed` — the strict pass failed but the
 * nullability-relaxed one recovered (the expected, benign case: a nullability
 * gap). `raw` — both failed, so this is a genuine type error and the boundary
 * fell back to the unparsed body.
 */
export type MismatchTier = 'relaxed' | 'raw';

/** Distinct fingerprints already reported this session. */
const seenFingerprints = new Set<string>();

/** Bound on `seenFingerprints` so a pathological session cannot grow it forever. */
const MAX_TRACKED_FINGERPRINTS = 500;

/** Distinct issues forwarded per response — one bad row shouldn't emit 40 events. */
const MAX_ISSUES_PER_RESPONSE = 5;

/** The zod first-party type tag of a node, or undefined if it has none. */
function typeNameOf(schema: z.ZodTypeAny): z.ZodFirstPartyTypeKind | undefined {
  return (schema?._def as { typeName?: z.ZodFirstPartyTypeKind } | undefined)?.typeName;
}

/**
 * Peel the wrappers that do NOT consume a path segment (optional, nullable,
 * default, effects, branded, readonly, lazy, pipeline) so the node underneath —
 * the one that actually owns the next key — is exposed. Bounded so a
 * self-referential `z.lazy` schema cannot spin here.
 */
function unwrapSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
  const K = z.ZodFirstPartyTypeKind;
  let current: z.ZodTypeAny = schema;
  for (let depth = 0; depth < 20 && current?._def; depth++) {
    const def = current._def as {
      typeName?: z.ZodFirstPartyTypeKind;
      innerType?: z.ZodTypeAny;
      type?: z.ZodTypeAny;
      schema?: z.ZodTypeAny;
      in?: z.ZodTypeAny;
      out?: z.ZodTypeAny;
      getter?: () => z.ZodTypeAny;
    };
    const t = def.typeName;
    let next: z.ZodTypeAny | undefined;
    if (
      t === K.ZodOptional ||
      t === K.ZodNullable ||
      t === K.ZodReadonly ||
      t === K.ZodDefault ||
      t === K.ZodCatch
    ) {
      next = def.innerType;
    } else if (t === K.ZodBranded) {
      next = def.type;
    } else if (t === K.ZodEffects) {
      next = def.schema;
    } else if (t === K.ZodPipeline) {
      // The OUTPUT shape is what a parsed value — and thus an issue path — sees.
      next = def.out ?? def.in;
    } else if (t === K.ZodLazy) {
      next = def.getter?.();
    } else {
      return current;
    }
    if (!next) {
      return current;
    }
    current = next;
  }
  return current;
}

/**
 * The child schema that owns `segment` on a single concrete node, or undefined
 * when it cannot be resolved (a container we do not model). Callers expand
 * unions/intersections first via {@link expandNodes}, so this only handles the
 * concrete containers a segment can descend into.
 */
function childSchemaAt(schema: z.ZodTypeAny, segment: string | number): z.ZodTypeAny | undefined {
  const K = z.ZodFirstPartyTypeKind;
  const t = typeNameOf(schema);
  if (t === K.ZodObject) {
    return (schema._def as { shape: () => z.ZodRawShape }).shape()[String(segment)];
  }
  if (t === K.ZodArray) {
    return (schema._def as { type: z.ZodTypeAny }).type;
  }
  if (t === K.ZodTuple) {
    const def = schema._def as { items: z.ZodTypeAny[]; rest?: z.ZodTypeAny | null };
    return typeof segment === 'number' ? (def.items[segment] ?? def.rest ?? undefined) : undefined;
  }
  return undefined;
}

/**
 * Resolve a set of nodes to the CONCRETE schemas that could own the next path
 * segment: unwrap non-consuming wrappers, then flatten `z.union` /
 * `z.discriminatedUnion` options and `z.intersection` sides.
 *
 * A record hidden in any branch must still be seen — a `discriminatedUnion` arm
 * or an intersection side emits issues with real, deep paths, so a `z.record`
 * nested under one would otherwise ship its key unmasked, the exact AMP-638
 * failure mode. The mask is conservative by design: a segment counts as a record
 * key if ANY resolved branch is a record. Bounded and de-duplicated so a cyclic
 * `z.lazy` schema cannot spin.
 */
function expandNodes(nodes: z.ZodTypeAny[]): z.ZodTypeAny[] {
  const K = z.ZodFirstPartyTypeKind;
  const out: z.ZodTypeAny[] = [];
  const queue = [...nodes];
  const seen = new Set<z.ZodTypeAny>();
  while (queue.length > 0 && out.length < 64) {
    const raw = queue.pop();
    if (!raw || seen.has(raw)) {
      continue;
    }
    seen.add(raw);
    const node = unwrapSchema(raw);
    const t = typeNameOf(node);
    if (t === K.ZodUnion || t === K.ZodDiscriminatedUnion) {
      // `_def.options` is an array for both (see relaxNullability's handling).
      queue.push(...(node._def as { options: z.ZodTypeAny[] }).options);
    } else if (t === K.ZodIntersection) {
      const def = node._def as { left: z.ZodTypeAny; right: z.ZodTypeAny };
      queue.push(def.left, def.right);
    } else {
      out.push(node);
    }
  }
  return out;
}

/**
 * Walk `schema` alongside an issue `path`, flagging each segment that is a
 * RECORD KEY. A `z.record`'s keys are arbitrary data — a record keyed by
 * customer email puts a live email in the path — whereas a `z.object`'s keys are
 * fixed schema structure. The path array alone cannot tell them apart (both are
 * strings), so this consults the schema (AMP-638).
 *
 * Best-effort by design: at the first segment whose owning schema cannot be
 * resolved (a container we do not model), tracking stops and the rest of the
 * mask is `false` — those segments keep the pre-existing numeric-only collapse.
 * Never throws: it feeds the boundary's own error reporter, which must not fail
 * while reporting a failure.
 */
function recordKeyMask(schema: z.ZodTypeAny, path: (string | number)[]): boolean[] {
  const K = z.ZodFirstPartyTypeKind;
  const mask: boolean[] = [];
  let nodes: z.ZodTypeAny[] = [schema];
  try {
    for (const segment of path) {
      const candidates = expandNodes(nodes);
      const isRecordKey = candidates.some((node) => typeNameOf(node) === K.ZodRecord);
      mask.push(isRecordKey);
      nodes = candidates.flatMap((node) => {
        if (typeNameOf(node) === K.ZodRecord) {
          return [(node._def as { valueType: z.ZodTypeAny }).valueType];
        }
        const child = childSchemaAt(node, segment);
        return child ? [child] : [];
      });
    }
  } catch {
    // A schema shape we cannot walk must not take down the reporter. Whatever we
    // resolved before the throw still collapses; the rest degrades to numeric-only.
  }
  while (mask.length < path.length) {
    mask.push(false);
  }
  return mask;
}

/**
 * The path for `extra`: the exact path, EXCEPT record keys, which are collapsed
 * to `*` because they are user data (a `notesByUser` key is a customer email —
 * AMP-638). Array indexes are kept exact here; only the fingerprint drops them.
 */
function pathOf(issue: z.ZodIssue, schema?: z.ZodTypeAny): string {
  if (issue.path.length === 0) {
    return '(root)';
  }
  const recordKeys = schema ? recordKeyMask(schema, issue.path) : undefined;
  return issue.path
    .map((segment, index) => (recordKeys?.[index] ? '*' : String(segment)))
    .join('.');
}

/**
 * The grouping path: array indexes AND record keys collapsed to `*`, so every
 * row of a drifted list — and every key of a drifted map — reports as the one
 * gap it actually is (and no record key, which may be PII, reaches the durable
 * fingerprint — AMP-638).
 */
function fingerprintPathOf(issue: z.ZodIssue, schema?: z.ZodTypeAny): string {
  if (issue.path.length === 0) {
    return '(root)';
  }
  const recordKeys = schema ? recordKeyMask(schema, issue.path) : undefined;
  return issue.path
    .map((segment, index) =>
      recordKeys?.[index] || typeof segment === 'number' ? '*' : String(segment),
    )
    .join('.');
}

/**
 * Always report a fingerprint the session has not seen; sample the repeats.
 *
 * Once the tracking set is full we can no longer tell new from repeat, so we
 * sample rather than report. Reporting would be the wrong guess: an untracked
 * fingerprint looks new on EVERY occurrence, so it would forward every time and
 * flood the very issue this budget protects.
 */
function shouldReport(fingerprint: string, tier: MismatchTier): boolean {
  if (seenFingerprints.has(fingerprint)) {
    return Math.random() < schemaMismatchSampleRates[tier];
  }

  if (seenFingerprints.size >= MAX_TRACKED_FINGERPRINTS) {
    return Math.random() < schemaMismatchSampleRates[tier];
  }

  seenFingerprints.add(fingerprint);
  return true;
}

/**
 * Which pass produced a mismatch, as the console labels it. Adds `strict` to
 * {@link MismatchTier}: the throwing boundary reports no tier to Sentry (it
 * raises instead), but the console still has to say why it spoke.
 */
type LoggedTier = MismatchTier | 'strict';

/** Issues rendered per response. Unchanged from the pre-AMP-593 console cap. */
const MAX_ISSUES_LOGGED = 25;

/** Path column width. Longer paths overflow it rather than truncate. */
const MAX_PATH_COLUMN = 44;

/** What the boundary DID, which is the thing a reader needs first. */
const TIER_HEADLINE: Record<LoggedTier, string> = {
  strict: 'strict kind — throwing',
  relaxed: 'recovered via relaxed parse',
  raw: 'NOT RECOVERED — returned raw, uncoerced body',
};

/**
 * The safe half of an issue's detail.
 *
 * `expected`/`received` are zod TYPE NAMES on `invalid_type` (`string`, `null`)
 * and the ACTUAL VALUE on every other code — the same asymmetry that keeps them
 * out of Sentry (see the module docblock). The console honours it identically:
 * type names print, real API data never does. Other codes render as path+code,
 * which still says where and what.
 */
function detailOf(issue: z.ZodIssue): string {
  return issue.code === 'invalid_type'
    ? `expected ${issue.expected}, received ${issue.received}`
    : '';
}

/** One aligned line per issue, under a header naming the schema and outcome. */
function formatMismatch(
  kind: string,
  error: z.ZodError,
  tier: LoggedTier,
  schema?: z.ZodTypeAny,
): string {
  const shown = error.errors.slice(0, MAX_ISSUES_LOGGED);
  const width = Math.min(
    Math.max(0, ...shown.map((issue) => pathOf(issue, schema).length)),
    MAX_PATH_COLUMN,
  );

  const lines = shown.map((issue) => {
    const detail = detailOf(issue);
    return `  ${pathOf(issue, schema).padEnd(width)}  ${issue.code}${detail ? `  ${detail}` : ''}`;
  });

  const dropped = error.errors.length - shown.length;
  if (dropped > 0) {
    lines.push(`  … ${dropped} more`);
  }

  const count = error.errors.length;
  return [
    `${kind} response schema mismatch (${TIER_HEADLINE[tier]}) — ${count} issue${count === 1 ? '' : 's'}:`,
    ...lines,
  ].join('\n');
}

/**
 * Console-render a mismatch for the local dev debugging it — the audience this
 * instrument exists for, and the one Sentry does not serve.
 *
 * ONE PRE-FORMATTED STRING, never an array of objects. Passing objects to
 * `console.error` renders them as `[Object, Object, …]` in a browser console,
 * which is exactly what this did before: E2E hit an eleven-issue mismatch and
 * the console reported eleven objects and no information.
 *
 * `raw` is LOUDER than `relaxed` on purpose — `error` vs `warn`, the same split
 * as the Sentry `level`. Relaxed means the boundary recovered the value with
 * coercions intact (benign, and the common case). Raw means it handed an
 * uncoerced body to a typed consumer, which is the money bug the tiering exists
 * to prevent; it should not read like routine noise.
 */
export function logSchemaMismatch(
  kind: string,
  error: z.ZodError,
  tier: LoggedTier = 'strict',
  schema?: z.ZodTypeAny,
): void {
  const rendered = formatMismatch(kind, error, tier, schema);
  if (tier === 'relaxed') {
    console.warn(rendered);
  } else {
    console.error(rendered);
  }
}

/**
 * A path+code summary of a secondary error, for context. Deliberately carries no
 * messages and no values — see the PII note in the module docblock.
 */
function summarizeIssues(error: z.ZodError, schema?: z.ZodTypeAny): string[] {
  return error.errors.slice(0, 10).map((issue) => `${pathOf(issue, schema)}:${issue.code}`);
}

/**
 * Log every mismatch to the console (unchanged local-dev behaviour) and forward
 * the distinct ones to Sentry.
 *
 * `schema` is the schema the response was parsed against. It is walked alongside
 * each issue path so record (map) keys — which can be PII, e.g. a customer email
 * — collapse to `*` rather than shipping to Sentry (AMP-638).
 *
 * `strictError` is the tier-1 error, passed at the raw tier only. Grouping there
 * is driven by the RELAXED error — those are the issues that actually forced the
 * fallback, so they are what a reader must act on — but the strict error says
 * which gaps the relaxed pass absorbed on the way, and losing that half makes
 * the loudest tier the least explicable.
 */
export function reportSchemaMismatch(
  kind: string,
  schema: z.ZodTypeAny,
  error: z.ZodError,
  tier: MismatchTier,
  strictError?: z.ZodError,
): void {
  logSchemaMismatch(kind, error, tier, schema);

  const seenThisResponse = new Set<string>();
  let forwarded = 0;

  for (const issue of error.errors) {
    if (forwarded >= MAX_ISSUES_PER_RESPONSE) {
      break;
    }

    const path = pathOf(issue, schema);
    const groupingPath = fingerprintPathOf(issue, schema);
    const fingerprintKey = `${kind}|${groupingPath}|${issue.code}`;
    if (seenThisResponse.has(fingerprintKey)) {
      continue;
    }
    seenThisResponse.add(fingerprintKey);

    // Only a FORWARDED event spends the budget. Counting sampled-out repeats
    // against it would let a handful of known gaps early in the error list
    // starve a genuinely new one later in the same response.
    if (!shouldReport(fingerprintKey, tier)) {
      continue;
    }
    forwarded++;

    // `expected`/`received` are only safe to forward on invalid_type, where both
    // are zod type names. On every other code `received` is the actual value —
    // real API data — so it stays out of Sentry. invalid_type is also the code
    // the nullability gaps we are hunting produce, so this costs us nothing on
    // the dominant path.
    const typeIssue: Partial<z.ZodInvalidTypeIssue> = issue.code === 'invalid_type' ? issue : {};

    const unrecognizedKeysIssue: Partial<z.ZodUnrecognizedKeysIssue> =
      issue.code === 'unrecognized_keys' ? issue : {};

    captureMessage(`API schema mismatch: ${kind}.${groupingPath} (${issue.code})`, {
      level: tier === 'raw' ? 'error' : 'warning',
      // The grouping key. Message text is deliberately excluded.
      fingerprint: ['api-schema-mismatch', kind, groupingPath, issue.code],
      tags: {
        'api.kind': kind,
        'api.path': groupingPath,
        'api.issue_code': issue.code,
        'api.tier': tier,
      },
      extra: {
        kind,
        path,
        code: issue.code,
        expected: typeIssue.expected,
        received: typeIssue.received,
        unrecognizedKeys: unrecognizedKeysIssue.keys,
        tier,
        // How the boundary resolved it, so a reader knows the user impact
        // without reading this file.
        outcome:
          tier === 'relaxed'
            ? 'recovered via nullability-relaxed parse; coercions applied'
            : 'relaxed parse also failed; returned raw response body',
        totalIssues: error.errors.length,
        // Raw tier only: the gaps the relaxed pass absorbed before hitting the
        // one above. Without these, the loudest tier says why tier 2 failed but
        // never why tier 1 did.
        ...(strictError
          ? {
              strictIssues: summarizeIssues(strictError, schema),
              strictTotalIssues: strictError.errors.length,
            }
          : {}),
      },
    });
  }
}

/** Reset the session dedupe state. Test hook. */
export function resetSchemaMismatchReporting(): void {
  seenFingerprints.clear();
}
